import { RefObject, useEffect } from 'react';

/** The slice of `MapRef` this needs, so it isn't tied to one map type. */
interface ResizableMapRef {
  getMap: () => { resize: () => void };
}

/**
 * Keeps the Mapbox canvas sized to its container.
 *
 * mapbox-gl only watches the window, so a container that changes size on its own
 * leaves the canvas at its old dimensions (mapbox-gl-js#9083).
 *
 * @param {RefObject} containerRef - The element wrapping the map.
 * @param {ResizableMapRef} mapRef - The map handle, once it exists.
 */
export const useMapResize = (
  containerRef: RefObject<HTMLElement | null>,
  mapRef: ResizableMapRef | null | undefined
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !mapRef) {
      return undefined;
    }
    const observer = new ResizeObserver(() => mapRef.getMap().resize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, mapRef]);
};
